import "react-responsive-modal/styles.css";
import { useCallback } from "react";
import Modal from "react-responsive-modal";
import EnterEmailComponent from "./modals/Components/EnterEmailComponent";
import EnterPasswordComponent from "./modals/Components/EnterPasswordComponent";
import RegisterComponent from "./modals/Components/RegisterComponent";
import OnboardingStep1 from "./modals/Components/OnboardingStep1Component";
import OnboardingStep2 from "./modals/Components/OnboardingStep2Component";
import OnboardingStep3 from "./modals/Components/OnboardingStep3Component";
import OnboardingStep4 from "./modals/Components/OnboardingStep4Component";
import OnboardingStep5 from "./modals/Components/OnboardingStep5Component";
import VerifyEmailComponent from "./modals/Components/VerifyEmailComponent";
import ForgotPasswordComponent from "./modals/Components/ForgotPasswordComponent";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import ResetForgotPasswordComponent from "./modals/Components/ResetForgotPasswordComponent";
import { useI18N } from "common/hooks";
import VerifyTermsAndConditions from "./modals/Components/VerifyTermsAndConditions";
import {
  showAcceptTAndCForNonLogin,
  termsAndConditionsAgreedKey,
} from "common/config";
import { trackEvent } from "common/analytics";

// Maps the active auth screen to the `auth_screen` value on the generated
// `auth_form_closed` event (vocabulary: auth_entry, credentials,
// email_verification, login, forgot_password — plus descriptive fallbacks).
const AUTH_SCREEN_BY_INDEX: Record<AuthModalScreen, string> = {
  [AuthModalScreen.EnterEmail]: "auth_entry",
  [AuthModalScreen.EnterPassword]: "login",
  [AuthModalScreen.Register]: "credentials",
  [AuthModalScreen.OnboardingStep1]: "onboarding",
  [AuthModalScreen.OnboardingStep2]: "onboarding",
  [AuthModalScreen.OnboardingStep3]: "onboarding",
  [AuthModalScreen.OnboardingStep4]: "onboarding",
  [AuthModalScreen.OnboardingStep5]: "onboarding",
  [AuthModalScreen.VerifyEmail]: "email_verification",
  [AuthModalScreen.ForgotPassword]: "forgot_password",
  [AuthModalScreen.ResetForgotPassword]: "forgot_password",
  [AuthModalScreen.OnboardingScreen]: "onboarding",
  [AuthModalScreen.VerifyTermsAndConditions]: "terms_and_conditions",
};

const AuthModals = () => {
  const { isAuthModalVisible, hideModal, screenIndex, setScreenIndex } =
    useAuthModals();
  const i18n = useI18N();

  // Check for Terms & Service acceptance
  const isTermsAccepted = useCallback(() => {
    const storedData = JSON.parse(
      localStorage.getItem(termsAndConditionsAgreedKey),
    );
    if (storedData) {
      const currentTime = new Date().getTime();
      const ninetyDaysInMs = 90 * 24 * 60 * 60 * 1000;
      return currentTime - storedData.timestamp < ninetyDaysInMs;
    }
    return false;
  }, []);

  return (
    <Modal
      focusTrapped
      animationDuration={0}
      blockScroll={false}
      open={isAuthModalVisible}
      onClose={() => {
        // Container dismiss (backdrop tap / Escape) — generated tracking plan:
        // auth_form_closed, tagged with the screen that was active.
        trackEvent("auth_form_closed", {
          auth_screen: AUTH_SCREEN_BY_INDEX[screenIndex],
        });
        hideModal();
      }}
      classNames={{
        root: "auth-modal-root",
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
      aria-labelledby="modal-title"
      aria-hidden={!isAuthModalVisible}
    >
      {screenIndex === AuthModalScreen.EnterEmail && (
        <EnterEmailComponent
          onClose={() => {
            console.log(
              "showAcceptTAndCForNonLogin",
              showAcceptTAndCForNonLogin,
              isTermsAccepted(),
            );
            if (showAcceptTAndCForNonLogin == "Yes" && !isTermsAccepted()) {
              setScreenIndex(AuthModalScreen.VerifyTermsAndConditions);
            } else {
              hideModal();
            }
          }}
          title={i18n.get("loginOrCreateAccount")}
        />
      )}

      {screenIndex === AuthModalScreen.EnterPassword && (
        <EnterPasswordComponent
          onClose={() => {
            hideModal();
          }}
          onBack={() => {
            setScreenIndex(AuthModalScreen.EnterEmail);
          }}
          title={i18n.get("login")}
        />
      )}

      {screenIndex === AuthModalScreen.Register && (
        <RegisterComponent
          onClose={() => {
            setScreenIndex(AuthModalScreen.EnterEmail);
          }}
          title={i18n.get("createAccount")}
        />
      )}

      {screenIndex === AuthModalScreen.OnboardingStep1 && (
        <OnboardingStep1
          title={i18n.get("profileSetup")}
          subtitle={i18n.get("helpUsImproveYourExperience")}
          onClose={() => hideModal()}
        />
      )}

      {screenIndex === AuthModalScreen.OnboardingStep2 && (
        <OnboardingStep2
          title={i18n.get("createYourAccessibilityProfile")}
          subtitle={i18n.get("tellUsaBitMoreAboutYourself")}
        />
      )}

      {screenIndex === AuthModalScreen.OnboardingStep3 && (
        <OnboardingStep3
          title={i18n.get("shareYourAccessibilityProfile")}
          subtitle={i18n.get("shareYourDisability")}
        />
      )}

      {screenIndex === AuthModalScreen.OnboardingStep4 && (
        <OnboardingStep4
          title={i18n.get("findThePlacesThatWorkForYou")}
          subtitle={i18n.get("setYourPreferredFilters")}
        />
      )}

      {screenIndex === AuthModalScreen.OnboardingStep5 && (
        <OnboardingStep5
          onClose={() => hideModal()}
          title={i18n.get("yourIntroductionToSociability")}
          subtitle={i18n.get("tellUsHowYouFoundUs")}
          onSkip={() => {}}
        />
      )}

      {screenIndex === AuthModalScreen.VerifyEmail && (
        <VerifyEmailComponent
          title={i18n.get("letsVerifyYourEmailAddress")}
          subtitle={i18n.get("weJustNeedToCheckTtsReallyYou")}
        />
      )}

      {screenIndex === AuthModalScreen.ForgotPassword && (
        <ForgotPasswordComponent
          onClose={() => hideModal()}
          title={i18n.get("forgotYourPassword")}
        />
      )}

      {screenIndex === AuthModalScreen.ResetForgotPassword && (
        <ResetForgotPasswordComponent
          onClose={() => hideModal()}
          title={i18n.get("resetPassword")}
        />
      )}

      {screenIndex === AuthModalScreen.VerifyTermsAndConditions && (
        <VerifyTermsAndConditions
          onClose={() => hideModal()}
          title={i18n.get("termsOfUse")}
        />
      )}
    </Modal>
  );
};

export default AuthModals;
